import React, { FunctionComponent } from "react"
import { useTranslation } from "react-i18next"

import IconPencil from "assets/img/icon-pencil.svg?react"
import pictogramArticle from "assets/img/pictogram/article.svg"

import Text from "components/Text/Text"
import Button from "components/Button/Button"
import { Grid, GridItem } from "components/Grid"
import { Flex, FlexItem } from "components/Flex"

import Pictogram from "components/Pictogram/Pictogram"
import { ArticleAny, Clamp } from "@/types"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import { getArticleDescriptionHeaderTextType } from "utils/utils"
import articleSidebars from "stores/articleSidebars"
import InfoButton from "components/InfoButton/InfoButton"
import { ArticleZts } from "models/ArticleZts"
import { ArticleMachineBase } from "models/ArticleMachineBase"

interface IArticleDetailsHeaderProps {
    article: ArticleAny
    descriptionClamp?: Clamp
    adjustableDescription?: boolean
    infoButton?: boolean
    onEdit?: Function
}

const ArticleDetailsHeader: FunctionComponent<React.PropsWithChildren<IArticleDetailsHeaderProps>> = ({
    article,
    descriptionClamp = 1,
    adjustableDescription,
    infoButton,
    onEdit
}) => {
    const { t } = useTranslation()

    function showArticleSidebar() {
        articleSidebars.show({ articleId: article.id })
    }

    return (
        <>
            <Grid
                columns="6.4rem auto"
                gap="2.4rem">
                <GridItem>
                    <Pictogram
                        src={article.thumbnailUri || pictogramArticle}
                        fullSizeSrc={article.imageUri}
                        cover={!!article.thumbnailUri} />
                </GridItem>
                <GridItem>
                    <Flex
                        justifyContent="space-between">
                        <FlexItem>
                            <Flex alignItems="center">
                                <Text type={adjustableDescription ? getArticleDescriptionHeaderTextType(article.description) : "h2"}>
                                    <ArticleDescription
                                        articleDescription={article.description}
                                        clamp={descriptionClamp}
                                    />
                                </Text>
                                {infoButton &&
                                    <FlexItem className="ml-1">
                                        <InfoButton onClick={showArticleSidebar} />
                                    </FlexItem>
                                }
                            </Flex>
                            {article instanceof ArticleZts && article.descriptionOverwritten &&
                                <div className="color-grey-500 mt-half">
                                    {t('article.notOriginalZtsDescription')}
                                </div>
                            }
                        </FlexItem>
                        {onEdit &&
                            <FlexItem className="ml-2">
                                <Button
                                    round
                                    theme="grey"
                                    icon={<IconPencil />}
                                    onClick={onEdit}
                                    data-testid="article-details-edit" />
                            </FlexItem>
                        }
                    </Flex>
                    <Text className="mt-1" type="p">{t('common.artNo')}: <span data-testid="article-number">{article.articleNo}</span></Text>
                    {article instanceof ArticleMachineBase && article.getHierarchyDescription()}
                </GridItem>
            </Grid>
        </>
    )
}

export default ArticleDetailsHeader
